import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Clock, ExternalLink } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { getList } from "@/lib/api";
import { IMG } from "@/lib/images";
import { PageHeader } from "@/components/Editorial";

const CATS = ["Toutes", "conférence", "salon", "consultation", "rencontre", "dédicace", "AAESAD", "événement", "atelier"];

export default function Agenda() {
  const [tab, setTab] = useState("upcoming");
  const [cat, setCat] = useState("Toutes");
  const { data: events = [], isLoading } = useQuery({ queryKey: ["events"], queryFn: () => getList("events") });

  useSeo({
    title: "Agenda",
    description: "L'agenda de Dorothée Lancelot : conférences, salons, rencontres, dédicaces et événements à venir et passés.",
    path: "/agenda",
  });

  const today = new Date().toISOString().slice(0, 10);
  let filtered = events.filter((e) => (tab === "upcoming" ? (e.date || "") >= today : (e.date || "") < today));
  if (cat !== "Toutes") filtered = filtered.filter((e) => e.category === cat);
  filtered.sort((a, b) => (tab === "upcoming" ? (a.date || "").localeCompare(b.date || "") : (b.date || "").localeCompare(a.date || "")));

  if (!isLoading && events.length === 0) return <Navigate to="/" replace />;

  return (
    <div>
      <PageHeader eyebrow="Rendez-vous" title="Agenda" intro="Retrouvez les prochaines dates : conférences, salons, rencontres, dédicaces et événements." image={IMG.scotland} />

      <section className="dl-container py-14">
        <div className="flex gap-3 mb-8">
          {[["upcoming", "À venir"], ["past", "Passés"]].map(([k, l]) => (
            <button key={k} data-testid={`agenda-tab-${k}`} onClick={() => setTab(k)}
              className={`px-6 py-2.5 text-[0.72rem] tracking-widest uppercase border transition-colors ${tab === k ? "bg-[#C6A76A] text-[#050814] border-[#C6A76A]" : "border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/70"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-12">
          {CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)}
              className={`px-3.5 py-1.5 text-xs tracking-wide capitalize border transition-colors ${cat === c ? "bg-[#0B1020] border-[#C6A76A] text-[#D4B978]" : "border-white/10 text-[#F3E9D2]/55 hover:border-[#C6A76A]/50"}`}>
              {c}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="text-[#F3E9D2]/50 italic">Aucun événement {tab === "upcoming" ? "à venir" : "passé"} pour le moment.</p>
        ) : (          <div className="space-y-4">
            {filtered.map((e) => (
              <div key={e.id} className="dl-card p-6 flex flex-col md:flex-row gap-6 items-start" data-testid={`event-${e.id}`}>
                {e.image_url && <img src={e.image_url} alt={e.title} className="w-full md:w-52 h-36 object-cover" loading="lazy" />}
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    {e.category && <span className="dl-eyebrow capitalize" style={{ fontSize: "0.6rem" }}>{e.category}</span>}
                    {e.date && <span className="text-[#D4B978] text-sm">{e.date}{e.time ? ` · ${e.time}` : ""}</span>}
                  </div>
                  <h3 className="serif text-2xl text-[#F3E9D2] mt-2">{e.title}</h3>
                  {e.location && <p className="text-sm text-[#F3E9D2]/55 mt-1 inline-flex items-center gap-1.5"><MapPin size={13} /> {e.location}</p>}
                  {e.description && <p className="text-sm text-[#F3E9D2]/70 mt-3">{e.description}</p>}
                  <div className="flex items-center gap-4 mt-4">
                    {e.price && <span className="text-sm text-[#D4B978]">{e.price}</span>}
                    {e.link && <a href={e.link} target="_blank" rel="noreferrer" className="dl-link text-sm inline-flex items-center gap-1">Détails <ExternalLink size={12} /></a>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
